import React, { useState, useEffect } from 'react';
import { XMarkIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

/**
 * Page-level "Explain Metrics" button + slide-over panel.
 *
 * Pattern borrowed from WATI's sales analytics: rather than making users hunt
 * for hover-only ⓘ icons on each metric, one discoverable button opens a
 * scrollable list of every metric on the page with a plain-English
 * definition, how it's computed, and any gotchas.
 *
 * Usage:
 *   <ExplainMetrics
 *     items={[
 *       {
 *         name: 'After-Hours %',
 *         definition: 'Conversations that started outside 09:00-18:00 IST.',
 *         calculation: 'count(conversations WHERE hour(created_at) NOT IN 9..17) / total',
 *         note: 'Business hours are hardcoded for now — see Phase 2 to make per-tenant.',
 *       },
 *       ...
 *     ]}
 *   />
 *
 * Renders a compact button by default; click opens a right-side slide-over.
 * Esc + backdrop click both close it. Anchor with position: fixed so it lives
 * outside the page layout.
 */
const ExplainMetrics = ({ items = [], buttonLabel = 'Explain metrics' }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!items.length) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-md px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
      >
        <QuestionMarkCircleIcon className="w-4 h-4" />
        {buttonLabel}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          {/* Panel */}
          <div className="relative w-full max-w-md bg-white dark:bg-gray-900 shadow-xl h-full overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                What these numbers mean
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-700 dark:hover:text-white"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-5">
              {items.map((item) => (
                <div key={item.name}>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    {item.name}
                  </div>
                  {item.definition && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-1.5">
                      {item.definition}
                    </p>
                  )}
                  {item.calculation && (
                    <details className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">
                      <summary className="cursor-pointer select-none">Show technical definition</summary>
                      <div className="mt-1 font-mono bg-gray-50 dark:bg-gray-800 rounded px-2 py-1">
                        {item.calculation}
                      </div>
                    </details>
                  )}
                  {item.note && (
                    <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                      Note: {item.note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExplainMetrics;
